// Client-side PDF export. Generates a COMPLETE, styled report from a ScanResult
// using jsPDF + jspdf-autotable: a cover page with a score gauge, a table of
// contents, numbered sections, severity-tinted tables, and a running
// header/footer. Not a render of the styled HTML; it lays the data out
// programmatically.
//
// Everything runs in the browser, so the scan result never leaves the user's
// machine ("nothing stored" holds). jsPDF + autotable (~430 KB) load on demand
// via dynamic import, so they stay out of the initial bundle.

import type { ScanResult, Remediation } from '../types';
import type { CellHookData } from 'jspdf-autotable';

type RGB = [number, number, number];

// Core palette.
const INK: RGB = [15, 23, 42];
const BODY: RGB = [51, 65, 85];
const MUTED: RGB = [100, 116, 139];
const FAINT: RGB = [148, 163, 184];
const ACCENT: RGB = [2, 132, 199];
const LINE: RGB = [226, 232, 240];
const LINE_STRONG: RGB = [203, 213, 225];
const PANEL: RGB = [248, 250, 252];
const HEAD_FILL: RGB = [15, 23, 42];

const POSTURE: Record<string, { color: RGB; label: string }> = {
  excellent: { color: [5, 150, 105], label: 'Excellent' },
  good: { color: [2, 132, 199], label: 'Good' },
  fair: { color: [217, 119, 6], label: 'Fair' },
  poor: { color: [225, 29, 72], label: 'Needs attention' }
};

// Tint pairs for status/severity table cells: [fill, text].
const TINT = {
  bad: { fill: [252, 226, 232] as RGB, text: [159, 18, 57] as RGB },
  warn: { fill: [254, 243, 219] as RGB, text: [146, 64, 14] as RGB },
  info: { fill: [224, 242, 254] as RGB, text: [7, 89, 133] as RGB },
  ok: { fill: [220, 245, 233] as RGB, text: [6, 95, 70] as RGB }
};
const STATUS_TINT: Record<string, { fill: RGB; text: RGB }> = {};
for (const k of ['CRITICAL', 'HIGH', 'FAIL', 'EXPOSED', 'GAP', 'MISSING', 'VULNERABLE']) STATUS_TINT[k] = TINT.bad;
for (const k of ['MEDIUM', 'PARTIAL', 'TESTING', 'NOT ENABLED', 'NOT SET']) STATUS_TINT[k] = TINT.warn;
for (const k of ['LOW', 'INFO', 'UNKNOWN']) STATUS_TINT[k] = TINT.info;
for (const k of ['PRESENT', 'CONFIGURED', 'ENABLED', 'SATISFIED', 'PASS', 'SAFE', 'OK']) STATUS_TINT[k] = TINT.ok;

function scoreColor(n?: number | null): RGB {
  if (n == null) return MUTED;
  if (n >= 8) return [5, 150, 105];
  if (n >= 6) return [2, 132, 199];
  if (n >= 4) return [217, 119, 6];
  return [225, 29, 72];
}

// jsPDF core fonts only cover Latin-1 (CP1252). Normalize smart punctuation to
// ASCII (Unicode escapes so no build tool can mangle them) and strip anything
// outside the byte range so scan data never renders as garbage glyphs.
function t(v: unknown): string {
  return String(v ?? '')
    .replace(/[‘’‛]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[–—―]/g, '-')
    .replace(/[•▸→▪‧]/g, '-')
    .replace(/[ ]/g, ' ')
    .replace(/[^\x09\x0a\x20-\xFF]/g, '');
}

function fmtDateTime(ms: number): string {
  try {
    return new Date(ms).toLocaleString(undefined, {
      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return new Date(ms).toISOString();
  }
}

function d(s?: string): string {
  if (!s) return '-';
  const mm = /^(\d{4}-\d{2}-\d{2})/.exec(s);
  return mm ? mm[1] : t(s);
}

type Doc = import('jspdf').jsPDF & { lastAutoTable?: { finalY: number } };

export interface PdfOptions {
  /** PNG/JPEG data URL for the cover + running-header logo. */
  logoDataUrl?: string;
}

export async function buildScanPdfDoc(scan: ScanResult, opts: PdfOptions = {}): Promise<Doc> {
  const [{ jsPDF }, autoTableMod] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);
  const autoTable = autoTableMod.default;
  const doc = new jsPDF({ unit: 'pt', format: 'letter' }) as Doc;

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 48;
  const contentW = pageW - margin * 2;
  const contentTop = 66;
  const contentBottom = pageH - 52;
  let y = contentTop;

  const m = scan.modules;
  const posture = POSTURE[scan.executive?.posture] ?? POSTURE.fair;
  const logo = opts.logoDataUrl;

  const setColor = (c: RGB) => doc.setTextColor(c[0], c[1], c[2]);
  const setDraw = (c: RGB) => doc.setDrawColor(c[0], c[1], c[2]);
  const setFill = (c: RGB) => doc.setFillColor(c[0], c[1], c[2]);

  // section table of contents: { num, title, page }
  const toc: { num: string; title: string; page: number }[] = [];
  let sectionNum = 0;

  // ---- layout primitives -------------------------------------------------
  const need = (h: number) => {
    if (y + h > contentBottom) {
      doc.addPage();
      y = contentTop;
    }
  };

  const section = (title: string) => {
    sectionNum++;
    const num = String(sectionNum).padStart(2, '0');
    need(52);
    y += 8;
    setFill(ACCENT);
    doc.rect(margin, y - 9, 3, 13, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    setColor(ACCENT);
    doc.text(num, margin + 10, y);
    const nw = doc.getTextWidth(num);
    setColor(INK);
    doc.text(t(title).toUpperCase(), margin + 10 + nw + 8, y);
    y += 7;
    setDraw(LINE_STRONG);
    doc.setLineWidth(0.8);
    doc.line(margin, y, pageW - margin, y);
    y += 16;
    toc.push({ num, title, page: doc.getNumberOfPages() });
  };

  const paragraph = (text: string, o?: { size?: number; color?: RGB; bold?: boolean; gap?: number }) => {
    const size = o?.size ?? 10;
    const lineH = size * 1.42;
    doc.setFont('helvetica', o?.bold ? 'bold' : 'normal');
    doc.setFontSize(size);
    setColor(o?.color ?? BODY);
    const lines = doc.splitTextToSize(t(text), contentW) as string[];
    for (const ln of lines) {
      need(lineH);
      doc.text(ln, margin, y);
      y += lineH;
    }
    y += o?.gap ?? 4;
  };

  const note = (text: string) => paragraph(text, { size: 9, color: MUTED, gap: 6 });

  const kv = (label: string, value: unknown) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    const labelW = 118;
    const valLines = doc.splitTextToSize(t(value), contentW - labelW) as string[];
    const h = Math.max(valLines.length * 12, 12);
    need(h);
    setColor(MUTED);
    doc.text(t(label), margin, y);
    doc.setFont('helvetica', 'normal');
    setColor(INK);
    doc.text(valLines, margin + labelW, y);
    y += h + 3;
  };

  const codeBlock = (text: string) => {
    doc.setFont('courier', 'normal');
    doc.setFontSize(8);
    const lines = doc.splitTextToSize(t(text), contentW - 20) as string[];
    const h = lines.length * 10 + 12;
    need(h + 2);
    setFill([246, 248, 251]);
    setDraw(LINE);
    doc.setLineWidth(0.5);
    doc.roundedRect(margin, y, contentW, h, 3, 3, 'FD');
    setColor([30, 41, 59]);
    doc.text(lines, margin + 10, y + 12);
    y += h + 6;
    doc.setFont('helvetica', 'normal');
  };

  const table = (head: string[], body: (string | number)[][], columnStyles?: Record<number, object>) => {
    if (body.length === 0) return;
    need(64);
    autoTable(doc, {
      startY: y,
      head: [head.map((h) => t(h))],
      body: body.map((r) => r.map((c) => (typeof c === 'number' ? c : t(c)))),
      theme: 'grid',
      styles: { font: 'helvetica', fontSize: 8.5, cellPadding: 4.5, textColor: INK, lineColor: LINE, lineWidth: 0.4, overflow: 'linebreak', valign: 'middle' },
      headStyles: { fillColor: HEAD_FILL, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8, cellPadding: 5 },
      alternateRowStyles: { fillColor: PANEL },
      columnStyles: columnStyles ?? {},
      margin: { left: margin, right: margin, top: contentTop },
      // Tint any cell whose whole value is a known status/severity token.
      didParseCell: (data: CellHookData) => {
        if (data.section !== 'body') return;
        const key = String(data.cell.raw ?? '').trim().toUpperCase();
        const tint = STATUS_TINT[key];
        if (tint) {
          data.cell.styles.textColor = tint.text;
          data.cell.styles.fillColor = tint.fill;
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.halign = 'center';
        }
      }
    });
    y = (doc.lastAutoTable?.finalY ?? y) + 16;
  };

  // Arc gauge: light background ring + a value arc for score/10, posture-colored.
  const gauge = (cx: number, cy: number, r: number, pct: number, color: RGB, thickness: number) => {
    doc.setLineCap('butt');
    setDraw(LINE_STRONG);
    doc.setLineWidth(thickness);
    doc.circle(cx, cy, r, 'S');
    if (pct > 0) {
      doc.setLineCap('round');
      setDraw(color);
      const start = -90;
      const end = start + Math.min(1, pct) * 360;
      let prev: [number, number] | null = null;
      for (let a = start; a <= end + 0.001; a += 4) {
        const rad = (a * Math.PI) / 180;
        const px = cx + r * Math.cos(rad);
        const py = cy + r * Math.sin(rad);
        if (prev) doc.line(prev[0], prev[1], px, py);
        prev = [px, py];
      }
      doc.setLineCap('butt');
    }
  };

  // Horizontal score bar.
  const gradeBar = (x: number, by: number, w: number, label: string, score?: number | null) => {
    const barY = by + 3;
    const trackH = 6;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    setColor(MUTED);
    doc.text(t(label).toUpperCase(), x, by);
    doc.setFont('helvetica', 'normal');
    setColor(INK);
    doc.setFontSize(9);
    doc.text(score == null ? '-' : score.toFixed(1), x + w, by, { align: 'right' });
    setFill(LINE);
    doc.roundedRect(x, barY + 4, w, trackH, 2, 2, 'F');
    const pct = score == null ? 0 : Math.max(0.03, Math.min(1, score / 10));
    setFill(scoreColor(score));
    doc.roundedRect(x, barY + 4, w * pct, trackH, 2, 2, 'F');
  };

  // Cover "at a glance" stat: big value + label + optional sublabel.
  const coverStat = (x: number, sy: number, value: number | string, label: string, sub?: string, color: RGB = INK) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(23);
    setColor(color);
    doc.text(String(value), x, sy);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    setColor(MUTED);
    doc.text(t(label).toUpperCase(), x, sy + 15, { charSpace: 0.3 });
    if (sub) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      setColor(FAINT);
      doc.text(t(sub), x, sy + 26);
    }
  };

  // ======================================================================
  // COVER (page 1)
  // ======================================================================
  {
    let cy = 64;
    // brand row
    let bx = margin;
    if (logo) {
      try { doc.addImage(logo, 'PNG', margin, cy - 14, 20, 20, 'logo', 'FAST'); bx = margin + 28; } catch { bx = margin; }
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    setColor(INK);
    doc.text('PROTBYTE', bx, cy + 1);
    setColor(ACCENT);
    doc.text('SENTINEL', bx + doc.getTextWidth('PROTBYTE '), cy + 1);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    setColor(MUTED);
    doc.text('CONFIDENTIAL', pageW - margin, cy + 1, { align: 'right' });
    cy += 12;
    setDraw(INK);
    doc.setLineWidth(1.2);
    doc.line(margin, cy, pageW - margin, cy);

    // title block
    cy += 74;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    setColor(ACCENT);
    doc.text('EXTERNAL SECURITY & COMPLIANCE ASSESSMENT', margin, cy, { charSpace: 1.2 });
    cy += 34;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(34);
    setColor(INK);
    doc.text(t(scan.domain), margin, cy);
    cy += 20;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    setColor(MUTED);
    doc.text(`Scanned ${fmtDateTime(scan.created_at)}`, margin, cy);

    // gauge + module bars row
    cy += 70;
    const gx = margin + 66;
    const gyc = cy + 6;
    gauge(gx, gyc, 52, scan.overall_score / 10, posture.color, 11);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(30);
    setColor(posture.color);
    doc.text(scan.overall_score.toFixed(1), gx, gyc + 4, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    setColor(FAINT);
    doc.text('OUT OF 10', gx, gyc + 20, { align: 'center' });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    setColor(posture.color);
    doc.text(posture.label.toUpperCase(), gx, gyc + 74, { align: 'center' });

    // module bars to the right of the gauge
    const barsX = margin + 156;
    const barsW = pageW - margin - barsX;
    let byy = cy - 22;
    const mods: [string, number | undefined | null][] = [
      ['DNS', m.dns?.score], ['Email', m.email?.score], ['Web', m.web?.score],
      ['TLS', m.ssl?.score], ['WHOIS', m.whois?.score]
    ];
    for (const [label, s] of mods) {
      gradeBar(barsX, byy, barsW, label, s);
      byy += 24;
    }

    // headline
    cy += 116;
    setDraw(LINE);
    doc.setLineWidth(0.8);
    doc.line(margin, cy, pageW - margin, cy);
    cy += 24;
    if (scan.executive?.headline) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      setColor(INK);
      const hl = doc.splitTextToSize(t(scan.executive.headline), contentW) as string[];
      doc.text(hl, margin, cy);
      cy += hl.length * 17;
    }

    // at a glance
    cy += 30;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    setColor(ACCENT);
    doc.text('AT A GLANCE', margin, cy, { charSpace: 1.2 });
    cy += 26;
    const sev = { critical: 0, high: 0, medium: 0, low: 0 };
    for (const a of scan.alerts ?? []) if (a.severity in sev) sev[a.severity as keyof typeof sev]++;
    const frameworks = scan.compliance?.frameworks ?? [];
    const openCtl = frameworks.reduce((n, f) => n + f.gap + f.partial, 0);
    const totalCtl = frameworks.reduce((n, f) => n + f.total, 0);
    const alertColor: RGB = sev.critical || sev.high ? [225, 29, 72] : sev.medium ? [217, 119, 6] : INK;
    const colGap = 22;
    const colW = (contentW - colGap * 2) / 3;
    const colX = (i: number) => margin + (i % 3) * (colW + colGap);
    const rowY = (i: number) => cy + Math.floor(i / 3) * 58;
    coverStat(colX(0), rowY(0), scan.alerts?.length ?? 0, 'Security alerts',
      `${sev.critical} critical  ${sev.high} high  ${sev.medium} med  ${sev.low} low`, alertColor);
    coverStat(colX(1), rowY(1), scan.executive?.top_fixes?.length ?? 0, 'Priority fixes');
    coverStat(colX(2), rowY(2), scan.remediations?.length ?? 0, 'Recommended fixes');
    coverStat(colX(3), rowY(3), m.subdomains?.count ?? 0, 'Subdomains discovered');
    coverStat(colX(4), rowY(4), m.techstack?.technologies.length ?? 0, 'Technologies detected');
    coverStat(colX(5), rowY(5), openCtl, 'Compliance gaps', totalCtl ? `of ${totalCtl} controls mapped` : undefined,
      openCtl ? [217, 119, 6] : [5, 150, 105]);

    // cover footer
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    setColor(FAINT);
    doc.text('Results are not stored. This report was generated in your browser.', margin, pageH - 54);
    doc.text('sentinel.protbyte.com', margin, pageH - 42);
  }

  // reserve the contents page (filled in at the end), then start content
  doc.addPage();
  const tocPage = doc.getNumberOfPages();
  doc.addPage();
  y = contentTop;

  // ======================================================================
  // Executive summary
  // ======================================================================
  if (scan.executive?.headline || scan.executive?.narrative) {
    section('Executive summary');
    if (scan.executive.narrative) paragraph(scan.executive.narrative, { size: 10.5, color: BODY, gap: 6 });
  }

  // Security alerts
  if ((scan.alerts?.length ?? 0) > 0) {
    section(`Security alerts (${scan.alerts.length})`);
    const rank = { critical: 0, high: 1, medium: 2, low: 3 } as Record<string, number>;
    const rows = [...scan.alerts]
      .sort((a, b) => (rank[a.severity] ?? 9) - (rank[b.severity] ?? 9))
      .map((a) => [a.severity.toUpperCase(), a.module.toUpperCase(), a.title + (a.description ? `\n${a.description}` : '')]);
    table(['Severity', 'Module', 'Finding'], rows, { 0: { cellWidth: 64 }, 1: { cellWidth: 74 } });
  }

  // Priority fixes
  if ((scan.executive?.top_fixes?.length ?? 0) > 0) {
    section('Top priority fixes');
    scan.executive.top_fixes.forEach((f, i) => {
      need(30);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10.5);
      setColor(INK);
      const num = `${i + 1}. `;
      doc.text(num, margin, y);
      const nw = doc.getTextWidth(num);
      const titleLines = doc.splitTextToSize(t(f.title), contentW - nw) as string[];
      doc.text(titleLines, margin + nw, y);
      y += titleLines.length * 13;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      setColor(MUTED);
      doc.text(`Effort: ${f.effort.toUpperCase()}   Impact: ${f.impact}`, margin + nw, y);
      y += 12;
      if (f.rationale) paragraph(f.rationale, { size: 9, color: BODY, gap: 8 });
      else y += 6;
    });
  }

  // DNS
  if (m.dns) {
    section('DNS & registry');
    const dns = m.dns;
    kv('DNSSEC', dns.dnssec?.enabled ? `Enabled (${dns.dnssec.ds_records ?? 0} DS record(s)${dns.dnssec.algorithm ? `, ${dns.dnssec.algorithm}` : ''})` : 'Not enabled');
    kv('Records', `${dns.ip_count} IP address(es), ${dns.mx_count} MX, ${dns.caa_count} CAA`);
    if (dns.nameservers) kv('Nameservers', `${dns.nameservers.count} host(s), ${dns.nameservers.providers.length} operator(s): ${dns.nameservers.providers.join(', ')}${dns.nameservers.single_operator ? ' (single operator)' : ''}`);
    const rows: string[][] = [];
    const r = dns.records;
    r.a?.forEach((v) => rows.push(['A', v]));
    r.aaaa?.forEach((v) => rows.push(['AAAA', v]));
    r.mx?.forEach((mx) => rows.push(['MX', `${mx.preference} ${mx.exchange}`]));
    r.ns?.forEach((v) => rows.push(['NS', v]));
    r.caa?.forEach((c) => rows.push(['CAA', `${c.flags} ${c.tag} "${c.value}"`]));
    r.txt?.forEach((v) => rows.push(['TXT', v]));
    if (r.soa) rows.push(['SOA', `${r.soa.mname} ${r.soa.rname} serial ${r.soa.serial}`]);
    table(['Type', 'Record'], rows, { 0: { cellWidth: 50, fontStyle: 'bold' } });
  }

  // Email
  if (m.email) {
    const e = m.email;
    section(`Email authentication (grade ${e.grade})`);
    if (e.microsoft?.is_microsoft) {
      kv('Microsoft 365 cloud', `${e.microsoft.label}${e.microsoft.sovereign ? '  (sovereign government cloud)' : ''}`);
    }
    const rows: string[][] = [];
    rows.push(['SPF', e.spf.configured ? 'Configured' : 'Missing',
      e.spf.configured ? `${e.spf.qualifier ?? ''} ${e.spf.dns_lookups ?? '?'} lookups${e.spf.evaluation && e.spf.evaluation !== 'ok' ? ` (${e.spf.evaluation})` : ''}`.trim() : '']);
    rows.push(['DKIM', e.dkim.configured ? 'Configured' : 'Missing',
      e.dkim.selectors?.map((s) => `${s.selector}${s.key_size ? ` (${s.key_size}-bit)` : ''}${s.testing ? ' testing' : ''}`).join(', ') ?? '']);
    rows.push(['DMARC', e.dmarc.configured ? 'Configured' : 'Missing',
      `${e.dmarc.configured ? `p=${e.dmarc.policy ?? 'none'} ` : ''}${e.dmarc.pct != null ? `pct=${e.dmarc.pct} ` : ''}${e.dmarc.platform?.name ? `via ${e.dmarc.platform.name}` : ''}`.trim()]);
    rows.push(['BIMI', e.bimi.configured ? 'Configured' : 'Not set', e.bimi.logo_url ?? '']);
    rows.push(['MTA-STS', e.mta_sts.configured ? 'Configured' : 'Not set', e.mta_sts.configured ? `mode=${e.mta_sts.mode ?? '?'}${e.mta_sts.mx_covered != null ? (e.mta_sts.mx_covered ? ', MX covered' : ', MX not fully covered') : ''}` : '']);
    rows.push(['TLS-RPT', e.tls_rpt.configured ? 'Configured' : 'Not set', e.tls_rpt.rua?.join(', ') ?? '']);
    table(['Mechanism', 'Status', 'Detail'], rows, { 0: { cellWidth: 68, fontStyle: 'bold' }, 1: { cellWidth: 76 } });
    if (e.spf.record) kv('SPF record', e.spf.record);
    if (e.dmarc.record) kv('DMARC record', e.dmarc.record);
  }

  // Web
  if (m.web) {
    const w = m.web;
    section('Web security');
    kv('URL', w.url);
    if (w.status != null) kv('Status', `${w.status}${w.redirected_to ? ` to ${w.redirected_to}` : ''}`);
    const HEADERS: [string, keyof typeof w.headers][] = [
      ['Strict-Transport-Security', 'hsts'], ['Content-Security-Policy', 'csp'],
      ['X-Frame-Options', 'x_frame_options'], ['X-Content-Type-Options', 'x_content_type_options'],
      ['Referrer-Policy', 'referrer_policy'], ['Permissions-Policy', 'permissions_policy'],
      ['X-XSS-Protection', 'x_xss_protection']
    ];
    const rows = HEADERS.map(([label, key]) => {
      const val = w.headers[key];
      return [label, val ? 'Present' : 'Missing', val ? String(val) : ''];
    });
    table(['Header', 'Status', 'Value'], rows, { 0: { cellWidth: 156 }, 1: { cellWidth: 60 } });
  }

  // SSL / TLS
  if (m.ssl) {
    const s = m.ssl;
    section('SSL / TLS certificates');
    kv('Certificates seen', `${s.cert_count} (via Certificate Transparency)`);
    if (s.recent_cert) {
      kv('Issuer', s.recent_cert.issuer_name);
      kv('Common name', s.recent_cert.common_name);
      kv('Valid', `${d(s.recent_cert.not_before)} to ${d(s.recent_cert.not_after)}`);
    }
    if (s.days_to_expiry != null) kv('Expires in', `${s.days_to_expiry} day(s)`);
  }

  // WHOIS
  if (m.whois) {
    const w = m.whois;
    section('WHOIS / RDAP');
    if (!w.found) {
      note(w.rate_limited ? 'The registry RDAP server rate-limited the lookup.' : 'No registry record was found.');
    } else {
      if (w.registrar) kv('Registrar', w.registrar);
      if (w.registry) kv('Registry', w.registry);
      kv('Registered', d(w.registered));
      kv('Expires', d(w.expires));
      if (w.updated) kv('Updated', d(w.updated));
      if (w.age_days != null) kv('Domain age', `${Math.max(0, Math.floor(w.age_days / 365))} year(s) (${w.age_days} days)`);
      if (w.status?.length) kv('Status', w.status.join(', '));
      if (w.nameservers?.length) kv('Nameservers', w.nameservers.join(', '));
    }
  }

  // Infrastructure
  if (m.infra && m.infra.hosts.length > 0) {
    const inf = m.infra;
    section('Infrastructure & ASN');
    if (inf.providers.length) kv('Providers', inf.providers.join(', '));
    kv('CDN-fronted', inf.cdn_fronted ? 'Yes' : 'No');
    const rows = inf.hosts.map((h) => [h.ip, h.ptr ?? '-', h.asn ? `AS${h.asn}${h.as_name ? ` ${h.as_name}` : ''}` : '-', h.provider ?? '-', h.country ?? '-']);
    table(['IP', 'Reverse DNS', 'ASN', 'Provider', 'Country'], rows, { 4: { cellWidth: 50, halign: 'center' } });
  }

  // Subdomains
  if (m.subdomains) {
    const sub = m.subdomains;
    section(`Subdomains (${sub.count})`);
    if (sub.wildcard_dns) note('A catch-all wildcard A record was detected; takeover detection is skipped for this zone.');
    if (sub.takeover_candidates?.length) {
      paragraph(`${sub.takeover_candidates.length} possible takeover candidate(s):`, { size: 9.5, bold: true, color: INK, gap: 4 });
      table(['Host', 'Dangling CNAME', 'Service', 'Confidence'],
        sub.takeover_candidates.map((tk) => [tk.host, tk.cname, tk.service ?? '-', tk.confidence.toUpperCase()]),
        { 3: { cellWidth: 66 } });
    }
    if (sub.notable?.length) {
      table(['Notable host', 'Category'], sub.notable.map((n) => [n.host, n.category]), { 1: { cellWidth: 124 } });
    }
    if (sub.hosts.length) {
      const shown = sub.hosts.slice(0, 60);
      kv('Discovered hosts', shown.join(', ') + (sub.hosts.length > shown.length ? `, and ${sub.hosts.length - shown.length} more` : ''));
    }
  }

  // Tech stack
  if (m.techstack && m.techstack.technologies.length > 0) {
    const tech = m.techstack;
    section(`Tech stack (${tech.technologies.length})`);
    const rows = tech.technologies
      .slice()
      .sort((a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name))
      .map((x) => [x.name, x.category, x.version ?? '-']);
    table(['Technology', 'Category', 'Version'], rows, { 2: { cellWidth: 72 } });
  }

  // Vendors
  if (m.vendors && (m.vendors.vendors.length > 0 || m.vendors.email_platform)) {
    const v = m.vendors;
    section(`Vendor surface (${v.total})`);
    const rows: string[][] = [];
    if (v.email_platform) rows.push([v.email_platform.name, `${v.email_platform.category} (email)`, v.email_platform.evidence]);
    v.vendors.forEach((vd) => rows.push([vd.name, vd.category, vd.evidence]));
    table(['Vendor', 'Category', 'Evidence'], rows, { 1: { cellWidth: 92 } });
  }

  // Vulnerabilities
  if (m.vulns && m.vulns.vulnerabilities.length > 0) {
    const v = m.vulns;
    section(`Vulnerabilities (${v.vulnerabilities.length})`);
    const rows = v.vulnerabilities.map((f) => [f.cve_id, `${f.package_name}@${f.package_version}`, (f.severity ?? '-').toUpperCase(), f.summary]);
    table(['CVE', 'Package', 'Severity', 'Summary'], rows, { 0: { cellWidth: 92 }, 2: { cellWidth: 60 } });
  }

  // Exposure
  if (m.exposure && m.exposure.findings.length > 0) {
    const ex = m.exposure;
    section(`Exposure checks (${ex.exposed}/${ex.checked} exposed)`);
    const rows = ex.findings
      .slice()
      .sort((a, b) => (a.status === 'exposed' ? 0 : 1) - (b.status === 'exposed' ? 0 : 1))
      .map((f) => [f.title, f.category, f.status.toUpperCase(), (f.severity ?? '-').toUpperCase()]);
    table(['Check', 'Category', 'Status', 'Severity'], rows, { 2: { cellWidth: 66 }, 3: { cellWidth: 60 } });
  }

  // Remediation
  if ((scan.remediations?.length ?? 0) > 0) {
    section(`Remediation (${scan.remediations.length})`);
    const order = { low: 0, medium: 1, high: 2 } as Record<string, number>;
    const sorted = [...scan.remediations].sort((a, b) => (order[a.effort] ?? 9) - (order[b.effort] ?? 9));
    sorted.forEach((r: Remediation, idx) => {
      need(44);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      setColor(INK);
      const titleLines = doc.splitTextToSize(`${idx + 1}. ${t(r.title)}`, contentW - 76) as string[];
      doc.text(titleLines, margin, y);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      setColor(FAINT);
      doc.text(`${r.effort.toUpperCase()} / ${r.impact}`, pageW - margin, y, { align: 'right' });
      y += titleLines.length * 12 + 3;
      if (r.summary) paragraph(r.summary, { size: 9, color: BODY, gap: 4 });
      r.steps?.forEach((s) => paragraph(`- ${s}`, { size: 8.5, color: MUTED, gap: 1 }));
      r.records?.forEach((rec) => codeBlock(`${rec.type === 'file' ? 'FILE' : rec.type} ${rec.host}\n${rec.value}`));
      r.snippets?.forEach((sn) => codeBlock(`# ${sn.label}\n${sn.code}`));
      y += 8;
    });
  }

  // Compliance
  if ((scan.compliance?.frameworks?.length ?? 0) > 0) {
    section('Compliance');
    table(['Framework', 'Satisfied', 'Partial', 'Gap', 'Total'],
      scan.compliance.frameworks.map((f) => [f.name, f.satisfied, f.partial, f.gap, f.total]),
      { 1: { cellWidth: 62, halign: 'center' }, 2: { cellWidth: 54, halign: 'center' }, 3: { cellWidth: 46, halign: 'center' }, 4: { cellWidth: 50, halign: 'center' } });
    note('Controls needing attention (gap or partial). Satisfied controls are omitted for brevity.');
    scan.compliance.frameworks.forEach((f) => {
      const open = f.families.flatMap((fam) => fam.controls).filter((c) => c.status === 'gap' || c.status === 'partial');
      if (open.length === 0) return;
      paragraph(f.name, { size: 9.5, bold: true, color: INK, gap: 3 });
      table(['Control', 'Name', 'Status'],
        open.map((c) => [c.control_id, c.control_name, c.status.toUpperCase()]),
        { 0: { cellWidth: 92 }, 2: { cellWidth: 60 } });
    });
  }

  // ======================================================================
  // Table of contents (fill the reserved page)
  // ======================================================================
  {
    doc.setPage(tocPage);
    let ty = contentTop + 6;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    setColor(INK);
    doc.text('Contents', margin, ty);
    ty += 10;
    setDraw(LINE_STRONG);
    doc.setLineWidth(0.8);
    doc.line(margin, ty, pageW - margin, ty);
    ty += 24;
    for (const item of toc) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      setColor(ACCENT);
      doc.text(item.num, margin, ty);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10.5);
      setColor(INK);
      const title = t(item.title);
      doc.text(title, margin + 24, ty);
      const titleW = doc.getTextWidth(title);
      doc.setFontSize(10);
      setColor(MUTED);
      const pageStr = String(item.page);
      const pageW2 = doc.getTextWidth(pageStr);
      // dotted leader
      const leaderStart = margin + 24 + titleW + 6;
      const leaderEnd = pageW - margin - pageW2 - 6;
      if (leaderEnd > leaderStart) {
        setDraw(LINE_STRONG);
        doc.setLineWidth(0.5);
        doc.setLineDashPattern([0.5, 2.5], 0);
        doc.line(leaderStart, ty - 2, leaderEnd, ty - 2);
        doc.setLineDashPattern([], 0);
      }
      doc.text(pageStr, pageW - margin, ty, { align: 'right' });
      ty += 22;
    }
  }

  // ======================================================================
  // Running header + footer on every page except the cover
  // ======================================================================
  const total = doc.getNumberOfPages();
  for (let p = 2; p <= total; p++) {
    doc.setPage(p);
    // header
    let hx = margin;
    if (logo) { try { doc.addImage(logo, 'PNG', margin, 20, 12, 12, 'logo', 'FAST'); hx = margin + 18; } catch { hx = margin; } }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    setColor(MUTED);
    doc.text('PROTBYTE SENTINEL', hx, 29);
    doc.setFont('helvetica', 'normal');
    setColor(FAINT);
    doc.text(t(scan.domain), pageW - margin, 29, { align: 'right' });
    setDraw(LINE);
    doc.setLineWidth(0.5);
    doc.line(margin, 38, pageW - margin, 38);
    // footer
    doc.setFontSize(8);
    setColor(FAINT);
    doc.text(`CONFIDENTIAL  ·  Generated ${fmtDateTime(Date.now())}`, margin, pageH - 30);
    doc.text(`Page ${p} of ${total}`, pageW - margin, pageH - 30, { align: 'right' });
  }

  return doc;
}

// Load the site logo, downscaled to a small PNG data URL for the cover +
// header. Downscaling (the source is 512px) keeps the embedded image tiny;
// combined with the addImage alias it's stored once. Best-effort: any failure
// yields undefined and the report renders without the mark.
async function loadLogoDataUrl(): Promise<string | undefined> {
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = '/logo.png';
    });
    const size = 96;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;
    ctx.drawImage(img, 0, 0, size, size);
    return canvas.toDataURL('image/png');
  } catch {
    return undefined;
  }
}

export async function exportScanPdf(scan: ScanResult, opts: PdfOptions = {}): Promise<void> {
  const logoDataUrl = opts.logoDataUrl ?? (await loadLogoDataUrl());
  const doc = await buildScanPdfDoc(scan, { ...opts, logoDataUrl });
  const dateStr = new Date(scan.created_at).toISOString().slice(0, 10);
  doc.save(`protbyte-sentinel-${scan.domain}-${dateStr}.pdf`);
}
